import { Button, VirtualizedMultiSelectCombobox } from '@roger/ui-components';
import { useMemo, useState } from 'react';
import { readDelimitedParam } from '../../utils/reportDisplay';

export type ReportMultiSelectOption = { id: string; label: string };

function sortOptions(options: ReportMultiSelectOption[]) {
  return [...options].sort((a, b) => {
    const labelComparison = a.label.localeCompare(b.label, undefined, {
      sensitivity: 'base',
    });
    return labelComparison || a.id.localeCompare(b.id);
  });
}

export function ReportMultiSelectFilter({
  label,
  value,
  options,
  allLabel,
  leadingOptions = [],
  onChange,
  deferApply = false,
  exclusiveOptionId,
}: {
  label: string;
  value: string;
  options: ReportMultiSelectOption[];
  allLabel: string;
  leadingOptions?: ReportMultiSelectOption[];
  onChange: (value: string) => void;
  deferApply?: boolean;
  exclusiveOptionId?: string;
}) {
  const selectedValues = useMemo(() => readDelimitedParam(value), [value]);
  const [open, setOpen] = useState(false);
  const [draftValues, setDraftValues] = useState(selectedValues);
  const activeValues = deferApply && open ? draftValues : selectedValues;
  const orderedOptions = useMemo(() => {
    const seenIds = new Set<string>();
    return [...leadingOptions, ...sortOptions(options)].filter(option => {
      if (seenIds.has(option.id)) return false;
      seenIds.add(option.id);
      return true;
    });
  }, [leadingOptions, options]);
  const comboboxOptions = useMemo(
    () =>
      orderedOptions.map(option => ({
        value: option.id,
        label: option.label,
      })),
    [orderedOptions]
  );
  const optionById = useMemo(
    () => new Map(orderedOptions.map(option => [option.id, option])),
    [orderedOptions]
  );
  const selectedLabels = activeValues.map(
    optionId => optionById.get(optionId)?.label ?? optionId
  );
  const ariaValue =
    selectedLabels.length > 0 ? selectedLabels.join(', ') : allLabel;

  return (
    <div className='space-y-1 text-sm font-medium'>
      <span>{label}</span>
      <VirtualizedMultiSelectCombobox
        options={comboboxOptions}
        value={activeValues}
        open={deferApply ? open : undefined}
        onOpenChange={
          deferApply
            ? nextOpen => {
                setOpen(nextOpen);
                if (nextOpen) setDraftValues(selectedValues);
              }
            : undefined
        }
        onValueChange={nextValue => {
          if (!deferApply) {
            onChange(nextValue.join(','));
            return;
          }
          setDraftValues(current => {
            const added = nextValue.find(item => !current.includes(item));
            if (!exclusiveOptionId || !added) return nextValue;
            if (added === exclusiveOptionId) return [exclusiveOptionId];
            return nextValue.filter(item => item !== exclusiveOptionId);
          });
        }}
        footer={
          deferApply ? (
            <div className='flex gap-2 p-2'>
              <Button
                type='button'
                variant='ghost'
                size='sm'
                className='flex-1 text-xs'
                onClick={() => setDraftValues([])}
              >
                Clear all ({draftValues.length})
              </Button>
              <Button
                type='button'
                size='sm'
                className='flex-1'
                onClick={() => {
                  onChange(draftValues.join(','));
                  setOpen(false);
                }}
              >
                Save
              </Button>
            </div>
          ) : undefined
        }
        placeholder={allLabel}
        searchPlaceholder={`Search ${label.toLowerCase()}...`}
        buttonClassName='w-full'
        ariaLabel={`${label}: ${ariaValue}`}
      />
    </div>
  );
}
